import { z } from 'zod';

/**
 * Task #1703 — Schema zod runtime per le risposte Feed v2 (topic, post report,
 * editor, audit log) introdotte con Task #1663. Source of truth dei tipi
 * pubblici esposti dalle rotte: `packages/backend/src/routes/messaging/feed.routes.ts`
 * legge il record SQL `RETURNING *` (più eventuali JOIN per le list view) e
 * lo serializza tale-quale tramite `res.json`.
 *
 * Convenzioni:
 *  - colonne `NOT NULL` da `RETURNING *`/SELECT → required.
 *  - colonne `NULL`-able → `.nullable()` (mai `.optional()`): vogliamo
 *    intercettare drop/rename, non perdonarli.
 *  - timestamp: la validazione gira sul payload prima di `JSON.stringify`,
 *    quindi pg ci passa `Date`; nei test (e in alcuni JOIN serializzati)
 *    arrivano come ISO string → `z.union([z.string().datetime(), z.date()])`.
 *  - aggregati pg `COUNT(*)` arrivano come `bigint` → stringa lato driver;
 *    ammessi anche come `number` se il code path normalizza.
 *  - `.passthrough()` su tutti gli schema: lato BE possiamo aggiungere
 *    colonne nuove (es. nuovo JOIN) senza rompere la response; il drift
 *    interessante (rename / drop di campo richiesto) viene comunque
 *    intercettato dai required.
 *  - JOIN fields sulle list view: `.nullable()` (LEFT JOIN può produrre
 *    null), MAI `.optional()` (devono essere presenti nella SELECT).
 */

const timestamp = z.union([z.string().datetime({ offset: true }), z.date()]);
const countLike = z.union([z.string(), z.number()]);

// ───── Topic ────────────────────────────────────────────────────────────────

/** Riga `feed_topics` ritornata da INSERT/UPDATE `RETURNING *`. */
export const FeedTopicSchema = z
  .object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    color: z.string().nullable(),
    icon: z.string().nullable(),
    is_active: z.boolean(),
    sort_order: z.number().int(),
    created_by: z.string().uuid().nullable(),
    created_at: timestamp,
    updated_at: timestamp,
  })
  .passthrough();
export type FeedTopic = z.infer<typeof FeedTopicSchema>;

/** Riga lista `GET /feed/topics`: include aggregati post/follower e flag personale. */
export const FeedTopicListItemSchema = FeedTopicSchema.extend({
  post_count: countLike,
  follower_count: countLike,
  is_following: z.boolean(),
});
export type FeedTopicListItem = z.infer<typeof FeedTopicListItemSchema>;

export const FeedTopicListResponseSchema = z.array(FeedTopicListItemSchema);
export type FeedTopicListResponse = z.infer<typeof FeedTopicListResponseSchema>;

// ───── Post Report ──────────────────────────────────────────────────────────

export const FEED_POST_REPORT_REASONS = [
  'spam', 'harassment', 'inappropriate', 'off_topic', 'misinformation', 'other',
] as const;
export const FeedPostReportReasonSchema = z.enum(FEED_POST_REPORT_REASONS);
export type FeedPostReportReason = z.infer<typeof FeedPostReportReasonSchema>;

export const FEED_POST_REPORT_STATUSES = ['open', 'reviewing', 'resolved', 'rejected'] as const;
export const FeedPostReportStatusSchema = z.enum(FEED_POST_REPORT_STATUSES);
export type FeedPostReportStatus = z.infer<typeof FeedPostReportStatusSchema>;

export const FEED_POST_REPORT_RESOLUTION_ACTIONS = [
  'none', 'delete_post', 'warn_author', 'dismiss',
] as const;
export const FeedPostReportResolutionActionSchema = z.enum(FEED_POST_REPORT_RESOLUTION_ACTIONS);
export type FeedPostReportResolutionAction = z.infer<typeof FeedPostReportResolutionActionSchema>;

/** Riga `feed_post_reports` ritornata da INSERT/UPDATE `RETURNING *`. */
export const FeedPostReportSchema = z
  .object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    post_id: z.string().uuid(),
    reporter_id: z.string().uuid(),
    reason: FeedPostReportReasonSchema,
    detail: z.string().nullable(),
    status: FeedPostReportStatusSchema,
    resolved_by: z.string().uuid().nullable(),
    resolved_at: timestamp.nullable(),
    resolution_note: z.string().nullable(),
    resolution_action: FeedPostReportResolutionActionSchema.nullable(),
    created_at: timestamp,
    updated_at: timestamp,
  })
  .passthrough();
export type FeedPostReport = z.infer<typeof FeedPostReportSchema>;

/** Riga `GET /feed/reports`: estende la base con JOIN su users + feed_posts. */
export const FeedPostReportListItemSchema = FeedPostReportSchema.extend({
  reporter_first_name: z.string().nullable(),
  reporter_last_name: z.string().nullable(),
  reporter_email: z.string().nullable(),
  resolver_first_name: z.string().nullable(),
  resolver_last_name: z.string().nullable(),
  post_title: z.string().nullable(),
  post_content_html: z.string().nullable(),
  post_author_id: z.string().uuid().nullable(),
  post_deleted_at: timestamp.nullable(),
  post_author_first_name: z.string().nullable(),
  post_author_last_name: z.string().nullable(),
});
export type FeedPostReportListItem = z.infer<typeof FeedPostReportListItemSchema>;

export const FeedPostReportListResponseSchema = z.array(FeedPostReportListItemSchema);
export type FeedPostReportListResponse = z.infer<typeof FeedPostReportListResponseSchema>;

// ───── Editor ───────────────────────────────────────────────────────────────

/** Riga `feed_editors` (tabella tenant_id+user_id, no `id` proprio). */
export const FeedEditorSchema = z
  .object({
    tenant_id: z.string().uuid(),
    user_id: z.string().uuid(),
    granted_by: z.string().uuid().nullable(),
    granted_at: timestamp,
  })
  .passthrough();
export type FeedEditor = z.infer<typeof FeedEditorSchema>;

/** Riga `GET /feed/editors`: estesa con dati anagrafici JOIN su users. */
export const FeedEditorListItemSchema = FeedEditorSchema.extend({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  granted_by_first_name: z.string().nullable(),
  granted_by_last_name: z.string().nullable(),
});
export type FeedEditorListItem = z.infer<typeof FeedEditorListItemSchema>;

export const FeedEditorListResponseSchema = z.array(FeedEditorListItemSchema);
export type FeedEditorListResponse = z.infer<typeof FeedEditorListResponseSchema>;

// ───── Audit Entry ──────────────────────────────────────────────────────────

/**
 * Riga `feed_audit_log` ritornata da `GET /feed/audit`. `id` è `bigserial`:
 * il driver pg lo restituisce come stringa; ammettiamo anche `number` per
 * sicurezza nel caso il code path lo coercizzi.
 */
export const FeedAuditEntrySchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    tenant_id: z.string().uuid(),
    actor_id: z.string().uuid().nullable(),
    actor_role: z.string().nullable(),
    action: z.string(),
    resource_type: z.string(),
    resource_id: z.string().nullable(),
    target_user_id: z.string().uuid().nullable(),
    detail: z.unknown(),
    created_at: timestamp,
  })
  .passthrough();
export type FeedAuditEntry = z.infer<typeof FeedAuditEntrySchema>;

/** Riga `GET /feed/audit`: estesa con JOIN su users (actor). */
export const FeedAuditEntryListItemSchema = FeedAuditEntrySchema.extend({
  actor_first_name: z.string().nullable(),
  actor_last_name: z.string().nullable(),
  actor_email: z.string().nullable(),
});
export type FeedAuditEntryListItem = z.infer<typeof FeedAuditEntryListItemSchema>;

export const FeedAuditListResponseSchema = z.array(FeedAuditEntryListItemSchema);
export type FeedAuditListResponse = z.infer<typeof FeedAuditListResponseSchema>;

// ───── Feed: suggerimenti topic, popolari, hashtag, upload ──────────────────
// Letti dal composer/sidebar/pagine Feed (desktop + mobile) tramite apiClient.

/** Elemento di `POST /feed/topics/suggest` → `data: FeedTopicSuggestion[]`. */
export const FeedTopicSuggestionSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  icon: z.string().nullable(),
  score: z.number(),
  reason: z.enum(['keyword', 'embedding', 'mixed']),
});
export type FeedTopicSuggestion = z.infer<typeof FeedTopicSuggestionSchema>;

/** Elemento di `GET /feed/popular` → `data: FeedPopularPost[]` (aggregati pg come stringa o numero). */
export const FeedPopularPostSchema = z
  .object({
    id: z.string(),
    title: z.string().nullable(),
    post_type: z.string().nullable().optional(),
    created_at: timestamp.optional(),
    author_first_name: z.string().nullable(),
    author_last_name: z.string().nullable(),
    reaction_count: countLike,
    comment_count: countLike,
    engagement: countLike.optional(),
  })
  .passthrough();
export type FeedPopularPost = z.infer<typeof FeedPopularPostSchema>;

/** Elemento di `GET /feed/hashtags/trending` → `data: FeedTrendingHashtag[]`. */
export const FeedTrendingHashtagSchema = z
  .object({
    tag: z.string(),
    post_count: countLike,
  })
  .passthrough();
export type FeedTrendingHashtag = z.infer<typeof FeedTrendingHashtagSchema>;

/**
 * `POST /feed/posts/upload` con il solo campo `file` (editor inline):
 * `data: FeedUploadedMedia`. Con il campo `files` la rotta risponde col post
 * completo (`FeedPost` del dominio FE).
 */
export const FeedUploadedMediaSchema = z
  .object({
    url: z.string(),
    key: z.string().optional(),
  })
  .passthrough();
export type FeedUploadedMedia = z.infer<typeof FeedUploadedMediaSchema>;

/** `GET /tasks/my-summary` → `data`: contatori del widget task nella sidebar Feed. */
export const FeedMyTasksSummarySchema = z.object({
  total: z.number(),
  overdue: z.number(),
  today: z.number(),
  in_progress: z.number(),
  delegated: z.number(),
  following: z.number(),
});
export type FeedMyTasksSummary = z.infer<typeof FeedMyTasksSummarySchema>;
